/**
 * Everything that differs between the Android app (Capacitor) and the browser (the web app on iPhones, todo 13):
 * one interface per capability, one implementation per platform. The rest of the app asks `platform` and never
 * checks the platform itself.
 */
import { App as NativeApp } from "@capacitor/app";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { askMotionPermission, nativeMotionSource, probeWebMotion, webMotionSource, type MotionSource } from "./ui/motionSource";

export interface Platform {
  name: "android" | "web";
  /** Saves a text file, given whole or as pieces that are joined; resolves false when the owner cancelled. */
  saveFile(name: string, text: string | string[], mimeType: string): Promise<boolean>;
  /** Lets the owner pick a text file and reads it; undefined when cancelled. Call it from a tap handler. */
  openTextFile(): Promise<string | undefined>;
  /**
   * A new motion source for recording mode and rep counting; undefined where the sensors give nothing (desktop
   * browser, iPhone without permission). Call it from a tap handler: Safari asks for the permission there.
   */
  motion(): Promise<MotionSource | undefined>;
  /** Whether `motion()` may give something (a phone): "Reps per Sensor zählen" is offered only there. */
  motionLikely(): boolean;
  /** Keeps the screen on until the returned release is called (taken again when the app comes back to the front). */
  keepAwake(): () => void;
  /** Called once at start: how the app's chimes mix with other apps' music. */
  setupAudioSession(): void;
  /** Called once at start: asks the browser not to delete the app's data when space runs low. */
  persistStorage(): void;
  /**
   * Called once at start: watches for a new version of the app (web app: a deploy). `onReady` gets a function that
   * switches to it and reloads the page; it is called when the new version is downloaded and waiting.
   */
  watchUpdates(onReady: (reload: () => void) => void): void;
  /** Hands the hardware back button to `onBack`; returns the cleanup. Without one (iPhone) a no-op. */
  onBackButton(onBack: () => void): () => void;
  /** Puts the app into the background, as the back button does on a start screen. */
  minimize(): void;
}

/**
 * android/app/src/main/java/.../SaveFilePlugin.java: the system's "save as" dialog, then the text in pieces (a motion
 * export or a big backup in one bridge call is too much for Android).
 */
const SaveFile = registerPlugin<{
  open(o: { name: string; mimeType: string }): Promise<{ opened: boolean }>;
  write(o: { text: string }): Promise<void>;
  close(): Promise<void>;
  abort(): Promise<void>;
}>("SaveFile");
/** characters per bridge call */
const SAVE_CHUNK = 256 * 1024;

/** Opens the system file picker through a file input; `accept` undefined shows every file. */
function pickTextFile(accept: string | undefined): Promise<string | undefined> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    if (accept) input.accept = accept;
    input.hidden = true;
    // Safari only opens pickers of inputs that are in the document
    document.body.append(input);
    const done = (text: string | undefined) => { input.remove(); resolve(text); };
    input.addEventListener("change", () => {
      const f = input.files?.[0];
      if (!f) return done(undefined);
      f.text().then(done, () => done(undefined));
    });
    // older browsers have no cancel event: the input then stays hidden in the document, which is harmless
    input.addEventListener("cancel", () => done(undefined));
    input.click();
  });
}

/** Screen Wake Lock API: the Android WebView and Safari (16.4+) both have it. */
function wakeLock(): () => void {
  if (!("wakeLock" in navigator)) return () => undefined;
  let lock: WakeLockSentinel | undefined;
  let stopped = false;
  const request = () => {
    if (document.visibilityState !== "visible" || (lock && !lock.released)) return;
    navigator.wakeLock.request("screen")
      .then((l) => { if (stopped) void l.release(); else lock = l; })
      .catch(() => { /* refused (e.g. battery saver): the screen may turn off */ });
  };
  request();
  document.addEventListener("visibilitychange", request);
  return () => {
    stopped = true;
    document.removeEventListener("visibilitychange", request);
    void lock?.release();
  };
}

/**
 * The service worker (src/sw.js, written by build.mjs) caches the build for offline use. A deploy is found when the
 * app starts or comes back to the front; the new worker then waits until `reload` lets it take over.
 */
function watchServiceWorker(onReady: (reload: () => void) => void) {
  // not in `npm run dev` (there is no sw.js, and cached files would hide the changes)
  if (process.env.NODE_ENV !== "production" || !("serviceWorker" in navigator)) return;
  const sw = navigator.serviceWorker;
  let reloading = false;
  // the first install takes over the page too: that is no update, so only a requested switch reloads
  sw.addEventListener("controllerchange", () => { if (reloading) location.reload(); });
  const offer = (worker: ServiceWorker) => onReady(() => { reloading = true; worker.postMessage("skipWaiting"); });
  const track = (worker: ServiceWorker | null) => {
    if (!worker) return;
    const check = () => { if (worker.state === "installed" && sw.controller) offer(worker); };
    worker.addEventListener("statechange", check);
    check();
  };
  sw.register("sw.js").then((reg) => {
    track(reg.waiting ?? reg.installing);
    reg.addEventListener("updatefound", () => track(reg.installing));
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") reg.update().catch(() => { /* offline */ });
    });
  }).catch((e) => console.error("service worker registration failed", e));
}

function download(name: string, parts: string[], mimeType: string) {
  const url = URL.createObjectURL(new Blob(parts, { type: mimeType }));
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const android: Platform = {
  name: "android",
  async saveFile(name, text, mimeType) {
    if (!(await SaveFile.open({ name, mimeType })).opened) return false;
    try {
      for (const part of typeof text === "string" ? [text] : text) {
        for (let i = 0; i < part.length; i += SAVE_CHUNK) await SaveFile.write({ text: part.slice(i, i + SAVE_CHUNK) });
      }
      await SaveFile.close();
    } catch (e) {
      // no half-written file left behind
      await SaveFile.abort().catch(() => undefined);
      throw e;
    }
    return true;
  },
  // Android's picker filters by MIME type, and files that came through a messenger or cloud app often carry a generic
  // one, so the app shows every file and checks the content itself
  openTextFile: () => pickTextFile(undefined),
  motion: async () => nativeMotionSource,
  motionLikely: () => true,
  keepAwake: wakeLock,
  // the WebView plays Web Audio without asking for audio focus: the chimes play over YouTube Music (todo 4)
  setupAudioSession() {},
  // the app's own storage: deleted only with the app
  persistStorage() {},
  // a new version comes as a new APK
  watchUpdates() {},
  onBackButton(onBack) {
    const listener = NativeApp.addListener("backButton", onBack);
    return () => void listener.then((l) => l.remove());
  },
  minimize() { void NativeApp.minimizeApp(); },
};

const web: Platform = {
  name: "web",
  async saveFile(name, text, mimeType) {
    // on a phone (iPhone: no downloads folder worth the name) the share sheet, with "In Dateien sichern";
    // on a desktop, or where sharing files is not possible, a download
    const parts = typeof text === "string" ? [text] : text;
    const file = new File(parts, name, { type: mimeType });
    const phone = matchMedia("(pointer: coarse)").matches;
    if (phone && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file] });
        return true;
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return false;
        // NotAllowedError (the tap is too long ago) and others: fall back to the download
      }
    }
    download(name, parts, mimeType);
    return true;
  },
  openTextFile: () => pickTextFile(".json,application/json"),
  // Chrome on Android sends devicemotion at about 60 Hz (todo 15: testing without a new APK); a desktop sends nothing
  async motion() {
    if (!(await askMotionPermission()) || !(await probeWebMotion())) return undefined;
    return webMotionSource();
  },
  motionLikely: () => "DeviceMotionEvent" in window && matchMedia("(pointer: coarse)").matches,
  keepAwake: wakeLock,
  setupAudioSession() {
    // Safari 16.4+: "ambient" mixes the chimes into other apps' music instead of stopping it (and follows the
    // silent switch); elsewhere there is no such setting
    const session = (navigator as { audioSession?: { type: string } }).audioSession;
    if (session) session.type = "ambient";
  },
  persistStorage() {
    // Safari grants it to a home-screen web app; a refusal changes nothing (the backup reminder is there for that)
    void navigator.storage?.persist?.().catch(() => false);
  },
  watchUpdates: watchServiceWorker,
  onBackButton: () => () => undefined,
  minimize() {},
};

export const platform: Platform = Capacitor.getPlatform() === "android" ? android : web;

/** Saves a JSON file. `pretty: false` writes it on one line (motion recordings: long number arrays). */
export function saveJson(name: string, data: unknown, pretty = true): Promise<boolean> {
  return platform.saveFile(name, pretty ? JSON.stringify(data, null, 2) : JSON.stringify(data), "application/json");
}
